import { useEffect, useRef, useState, type CSSProperties } from 'react';

export interface PhotoCarouselPhoto {
  url: string;
  /** Focal point in %, used as the image's object-position. */
  focal?: { x: number; y: number } | null;
}

export interface PhotoCarouselProps {
  photos: PhotoCarouselPhoto[];
  /** Alt text applied to every slide. */
  alt?: string;
  style?: CSSProperties;
}

const CSS = `
.sk-carousel-track { scrollbar-width: none; -ms-overflow-style: none; }
.sk-carousel-track::-webkit-scrollbar { display: none; }
`;

/**
 * PhotoCarousel — swipeable photo strip that fills its (positioned) parent.
 * Native scroll-snap, so it swipes on touch and scrolls on desktop trackpads;
 * the dots reflect and control the current slide. One photo renders as a plain
 * image with no dots. Images are lazy-loaded after the first.
 */
export function PhotoCarousel({ photos, alt = '', style }: PhotoCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const count = photos.length;

  useEffect(() => { setIndex(0); trackRef.current?.scrollTo({ left: 0 }); }, [count]);

  if (count === 0) return null;

  function onScroll() {
    const el = trackRef.current;
    if (!el || !el.clientWidth) return;
    setIndex(Math.max(0, Math.min(count - 1, Math.round(el.scrollLeft / el.clientWidth))));
  }

  function goTo(i: number) {
    const el = trackRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  }

  return (
    <div style={{ position: 'absolute', inset: 0, ...style }}>
      <style>{CSS}</style>
      <div
        ref={trackRef}
        className="sk-carousel-track"
        onScroll={onScroll}
        style={{
          display: 'flex', width: '100%', height: '100%',
          overflowX: count > 1 ? 'auto' : 'hidden', scrollSnapType: 'x mandatory',
          overscrollBehaviorX: 'contain',
        }}
      >
        {photos.map((p, i) => (
          <img
            key={p.url}
            src={p.url}
            alt={alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            draggable={false}
            style={{
              flex: '0 0 100%', width: '100%', height: '100%', objectFit: 'cover',
              scrollSnapAlign: 'center', scrollSnapStop: 'always',
              objectPosition: p.focal ? `${p.focal.x}% ${p.focal.y}%` : undefined,
            }}
          />
        ))}
      </div>
      {count > 1 ? (
        <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5, pointerEvents: 'none' }}>
          {photos.map((p, i) => (
            <button className="sk-hit"
              key={p.url}
              type="button"
              aria-label={`Photo ${i + 1} of ${count}`}
              onClick={(e) => { e.stopPropagation(); goTo(i); }}
              style={{
                pointerEvents: 'auto', padding: 0, border: 0, cursor: 'pointer',
                width: i === index ? 16 : 6, height: 6, borderRadius: 999,
                background: i === index ? 'rgb(var(--cream) / .95)' : 'rgb(var(--cream) / .5)',
                boxShadow: '0 0 4px rgba(0,0,0,.35)', transition: 'all .25s ease',
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
